// Modal editor for a column's list/table DISPLAY format (the "Display as"
// panel). Mirrors the LookupEditor / ValidationRuleEditor pattern: local draft
// state, a friendly per-format option panel, a LIVE preview that reuses the
// real <DisplayCell> renderer, and an onSave callback that hands back the
// DisplayFormat (or undefined when set to plain text).

import { useEffect, useMemo, useState } from 'react'

import { DisplayCell, type DisplayFormat, type DisplayFormatType, type DisplayMapEntry, type DisplayRule } from '../txn/displayFormat'
import type { ColumnDef } from '../txn/types'

import { ColorPickerField } from './ColorPickerField'

interface Props {
  open: boolean
  onClose: () => void
  column: ColumnDef | null
  // Sibling column names on the same form — for companion-column pickers.
  formColumns: string[]
  onSave: (display: DisplayFormat | undefined) => void
}

// "Display as" catalogue, grouped for the picker. label = friendly name,
// hint = one-liner shown under the picker.
const FORMAT_GROUPS: Array<{ group: string; items: Array<{ value: DisplayFormatType; label: string; hint: string }> }> = [
  { group: 'Plain', items: [
    { value: 'text', label: 'Plain text', hint: 'No special formatting (default).' },
  ] },
  { group: 'Numbers', items: [
    { value: 'number', label: 'Number / currency', hint: 'Thousands, decimals, currency, percent, compact (1.2K).' },
    { value: 'signed', label: 'Signed delta (+/−)', hint: 'Coloured ▲▼ with sign — for variance columns.' },
    { value: 'data_bar', label: 'Data bar', hint: 'A bar drawn behind the number to eyeball magnitude.' },
    { value: 'progress', label: 'Progress bar', hint: 'Horizontal bar filling from min to max.' },
    { value: 'percent_ring', label: 'Percent ring', hint: 'Circular completeness ring around the value.' },
    { value: 'heatmap', label: 'Heatmap', hint: 'Colour the text/background across value bands.' },
    { value: 'rating', label: 'Rating', hint: 'Stars / hearts / dots out of N.' },
    { value: 'bullet', label: 'Target vs actual', hint: 'Bullet bar: actual fill with a target tick.' },
    { value: 'sparkline', label: 'Sparkline', hint: 'Tiny line chart from an array / comma list.' },
  ] },
  { group: 'Status', items: [
    { value: 'status_tag', label: 'Status tag', hint: 'Coloured pill from a value → colour map.' },
    { value: 'status_dot', label: 'Status dot', hint: 'A coloured dot; value shows on hover.' },
    { value: 'icon_map', label: 'Icon map', hint: 'Map each value to an icon glyph.' },
    { value: 'boolean', label: 'Yes / No', hint: '✓ / ✗ (or a custom value map).' },
    { value: 'tags', label: 'Tags / chips', hint: 'Split a comma/array value into coloured chips.' },
  ] },
  { group: 'Date & time', items: [
    { value: 'date', label: 'Date / time', hint: 'Format with a pattern, e.g. dd-MMM-yyyy.' },
    { value: 'relative_time', label: 'Relative time', hint: '"3 days ago" / "in 2h".' },
    { value: 'date_range', label: 'Date range', hint: 'Start + an end column shown as one cell.' },
    { value: 'countdown', label: 'Countdown / SLA', hint: 'Time left to a due date, coloured by urgency.' },
    { value: 'elapsed', label: 'Elapsed (live)', hint: 'A ticking clock since a start time — a ticket\'s age, a wait — amber / red by minutes.' },
  ] },
  { group: 'Text', items: [
    { value: 'case', label: 'Change case', hint: 'UPPER / lower / Title / camelCase / Sentence.' },
    { value: 'truncate', label: 'Truncate', hint: 'Clamp long text with a hover tooltip.' },
    { value: 'masked', label: 'Masked', hint: 'Hide sensitive value (••••1234), click to reveal.' },
    { value: 'template', label: 'Template', hint: 'Wrap value in a pattern, e.g. INV-{value}.' },
    { value: 'two_line', label: 'Two-line', hint: 'Bold primary + a muted secondary column.' },
  ] },
  { group: 'Contact & media', items: [
    { value: 'telephone', label: 'Telephone', hint: 'Click-to-call; shows the country flag from the dial code (+91 → 🇮🇳).' },
    { value: 'country', label: 'Country', hint: 'Flag + name from an ISO code, dial code or country name.' },
    { value: 'email', label: 'Email', hint: 'Envelope icon, click to compose.' },
    { value: 'url', label: 'Link', hint: 'Open the URL in a new tab.' },
    { value: 'image', label: 'Image thumbnail', hint: 'Render an image URL as a thumbnail.' },
    { value: 'file', label: 'File', hint: 'Filename + icon, click to open.' },
    { value: 'avatar', label: 'Avatar', hint: 'Initials avatar + name (+ secondary).' },
    { value: 'qr', label: 'QR code', hint: 'Render the value as a scannable QR.' },
    { value: 'map', label: 'Map pin', hint: 'Open the location on a map in a new tab.' },
    { value: 'copy', label: 'Copy to clipboard', hint: 'Value with a copy affordance.' },
  ] },
  { group: 'Conditional', items: [
    { value: 'background', label: 'Conditional background', hint: 'Colour the cell by rules over other columns.' },
  ] },
]

const ALL_ITEMS = FORMAT_GROUPS.flatMap((g) => g.items)

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
}
const lbl: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, marginTop: 10 }
const input: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 14, boxSizing: 'border-box',
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const smallBtn: React.CSSProperties = {
  padding: '4px 10px', fontSize: 13, borderRadius: 5, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
}

export default function DisplayFormatEditor({ open, onClose, column, formColumns, onSave }: Props) {
  const [df, setDf] = useState<DisplayFormat>({ type: 'text' })
  const [sample, setSample] = useState('1234.5')
  const [sample2, setSample2] = useState('2000')

  useEffect(() => {
    if (!open) return
    setDf(column?.display ? { ...column.display } : { type: 'text' })
  }, [open, column])

  const patch = (p: Partial<DisplayFormat>) => setDf((d) => ({ ...d, ...p }))
  const meta = ALL_ITEMS.find((i) => i.value === df.type)

  // Build a preview row: the cell value + one companion sample mapped to any
  // referenced companion column, so composite formats render meaningfully.
  const previewCol = useMemo<ColumnDef>(() => ({ ...(column as ColumnDef), display: df }), [column, df])
  const companionRef = df.maxColumn || df.endColumn || df.secondaryColumn || df.avatarColumn || df.hrefColumn
  const previewRow = useMemo<Record<string, unknown>>(() => {
    const row: Record<string, unknown> = {}
    if (column?.db_name) row[column.db_name] = sample
    if (column?.name) row[column.name] = sample
    if (companionRef) { row[companionRef] = sample2 }
    return row
  }, [column, sample, sample2, companionRef])

  const allColDefs = useMemo<ColumnDef[]>(() => {
    const base = column ? [column] : []
    const extra = formColumns.map((n) => ({ name: n, db_name: n } as ColumnDef))
    return [...base, ...extra]
  }, [column, formColumns])

  if (!open || !column) return null

  const save = () => { onSave(df.type === 'text' ? undefined : df); onClose() }
  const clear = () => { onSave(undefined); onClose() }

  const colSelect = (label: string, key: keyof DisplayFormat, help?: string) => (
    <>
      <label style={lbl}>{label}</label>
      <select style={input} value={(df[key] as string) || ''} onChange={(e) => patch({ [key]: e.target.value || undefined } as Partial<DisplayFormat>)}>
        <option value="">— select column —</option>
        {formColumns.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {help && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3 }}>{help}</div>}
    </>
  )

  return (
    <div style={overlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--color-bg-secondary)', color: 'var(--color-text)', borderRadius: 8,
        width: 'min(760px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 12px 48px rgba(0,0,0,0.35)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid var(--color-border)' }}>
          <span style={{ fontWeight: 600 }}>Display format · <span style={{ color: 'var(--color-text-secondary)' }}>{column.heading || column.name}</span></span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2625rem', color: 'var(--color-text-secondary)' }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 0, overflow: 'hidden', flex: 1 }}>
          {/* Options */}
          <div style={{ padding: '4px 18px 18px', overflowY: 'auto' }}>
            <label style={lbl}>Display as</label>
            <select style={input} value={df.type} onChange={(e) => setDf({ type: e.target.value as DisplayFormatType })}>
              {FORMAT_GROUPS.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map((it) => <option key={it.value} value={it.value}>{it.label}</option>)}
                </optgroup>
              ))}
            </select>
            {meta && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>{meta.hint}</div>}

            {renderOptions(df, patch, colSelect, formColumns)}
          </div>

          {/* Live preview */}
          <div style={{ borderLeft: '1px solid var(--color-border)', padding: '12px 14px', background: 'var(--color-bg)', overflowY: 'auto' }}>
            <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-secondary)', marginBottom: 8 }}>Live preview</div>
            <div style={{ padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg-secondary)', minHeight: 40, display: 'flex', alignItems: 'center' }}>
              {df.type === 'text'
                ? <span style={{ color: 'var(--color-text-secondary)' }}>{sample || '—'}</span>
                : <DisplayCell value={sample} col={previewCol} row={previewRow} columns={allColDefs} />}
            </div>
            <label style={lbl}>Sample value</label>
            <input style={input} value={sample} onChange={(e) => setSample(e.target.value)} />
            {companionRef && (
              <>
                <label style={lbl}>Sample for “{companionRef}”</label>
                <input style={input} value={sample2} onChange={(e) => setSample2(e.target.value)} />
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--color-border)' }}>
          <button onClick={clear} style={{ ...smallBtn, color: 'var(--color-error, #dc2626)' }}>Remove format</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={smallBtn}>Cancel</button>
            <button onClick={save} style={{ ...smallBtn, border: 'none', background: 'var(--color-accent)', color: '#fff', fontWeight: 600 }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Per-format option fields ─────────────────────────────────────────────────

function renderOptions(
  df: DisplayFormat,
  patch: (p: Partial<DisplayFormat>) => void,
  colSelect: (label: string, key: keyof DisplayFormat, help?: string) => React.ReactNode,
  _formColumns: string[],
): React.ReactNode {
  const numFields = (
    <>
      <label style={lbl}>Number style</label>
      <select style={input} value={df.numberStyle || 'decimal'} onChange={(e) => patch({ numberStyle: e.target.value as DisplayFormat['numberStyle'] })}>
        <option value="decimal">Decimal (1,234.5)</option>
        <option value="currency">Currency (₹1,234.50)</option>
        <option value="accounting">Accounting (negatives in parens)</option>
        <option value="percent">Percent (12%)</option>
        <option value="compact">Compact (1.2K)</option>
        <option value="scientific">Scientific (1.2e3)</option>
      </select>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <div>
          <label style={lbl}>Decimals</label>
          <input style={input} type="number" min={0} max={8} value={df.decimals ?? ''} placeholder="auto" onChange={(e) => patch({ decimals: e.target.value === '' ? undefined : Number(e.target.value) })} />
        </div>
        <div>
          <label style={lbl}>Currency</label>
          <input style={input} value={df.currency || ''} placeholder="INR" onChange={(e) => patch({ currency: e.target.value || undefined })} />
        </div>
        <div>
          <label style={lbl}>Locale</label>
          <input style={input} value={df.locale || ''} placeholder="en-IN" onChange={(e) => patch({ locale: e.target.value || undefined })} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><label style={lbl}>Prefix</label><input style={input} value={df.prefix || ''} onChange={(e) => patch({ prefix: e.target.value || undefined })} /></div>
        <div><label style={lbl}>Suffix</label><input style={input} value={df.suffix || ''} onChange={(e) => patch({ suffix: e.target.value || undefined })} /></div>
      </div>
    </>
  )

  const minMax = (withTargetCol: boolean) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <div><label style={lbl}>Min</label><input style={input} type="number" value={df.min ?? ''} placeholder="0" onChange={(e) => patch({ min: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
      <div><label style={lbl}>Max</label><input style={input} type="number" value={df.max ?? ''} placeholder="100" onChange={(e) => patch({ max: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
      {withTargetCol && <div style={{ gridColumn: '1 / -1' }}>{colSelect('…or read max/target from column', 'maxColumn')}</div>}
    </div>
  )

  switch (df.type) {
    case 'number':
      return numFields
    case 'signed':
    case 'data_bar':
      return <>{numFields}{df.type === 'data_bar' && minMax(false)}{df.type === 'data_bar' && <ColorRow label="Bar colour" value={df.color} onChange={(c) => patch({ color: c })} />}</>
    case 'progress':
    case 'percent_ring':
      return <>{minMax(true)}<ColorRow label="Fill colour" value={df.color} onChange={(c) => patch({ color: c })} />{toggle('Show value', df.showValue !== false, (v) => patch({ showValue: v }))}</>
    case 'rating':
      return <>
        <label style={lbl}>Icon</label>
        <select style={input} value={df.icon || 'star'} onChange={(e) => patch({ icon: e.target.value })}>
          <option value="star">★ Stars</option><option value="heart">❤ Hearts</option><option value="circle">● Dots</option>
        </select>
        <div><label style={lbl}>Out of</label><input style={input} type="number" min={1} max={10} value={df.max ?? 5} onChange={(e) => patch({ max: Number(e.target.value) })} /></div>
        <ColorRow label="Colour" value={df.color} onChange={(c) => patch({ color: c })} />
      </>
    case 'bullet':
      return <>{colSelect('Target column', 'maxColumn', 'The goal each row is measured against.')}<ColorRow label="Bar colour" value={df.color} onChange={(c) => patch({ color: c })} /></>
    case 'sparkline':
      return <><div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>The cell value should be a JSON array or a comma list, e.g. <code>12,18,9,22</code>.</div><ColorRow label="Line colour" value={df.color} onChange={(c) => patch({ color: c })} /></>
    case 'heatmap':
      return <>
        <label style={lbl}>Scale</label>
        <select style={input} value={df.scale || 'stepped'} onChange={(e) => patch({ scale: e.target.value as DisplayFormat['scale'] })}>
          <option value="stepped">Stepped bands</option>
          <option value="gradient">Continuous gradient</option>
        </select>
        <label style={lbl}>Apply colour to</label>
        <select style={input} value={df.applyTo || 'text'} onChange={(e) => patch({ applyTo: e.target.value as DisplayFormat['applyTo'] })}>
          <option value="text">Text</option><option value="background">Background</option>
        </select>
        {df.scale === 'gradient'
          ? <>{minMax(false)}<ColorRow label="Low colour" value={df.colors?.[0]} onChange={(c) => patch({ colors: [c, df.colors?.[1] || '#dc2626'] })} /><ColorRow label="High colour" value={df.colors?.[1]} onChange={(c) => patch({ colors: [df.colors?.[0] || '#16a34a', c] })} /></>
          : <ThresholdEditor df={df} patch={patch} />}
        {numFields}
      </>
    case 'status_tag':
    case 'status_dot':
    case 'icon_map':
    case 'boolean':
    case 'tags':
      return <>
        <MapEditor df={df} patch={patch} withIcon={df.type !== 'status_tag' || true} />
        <ColorRow label="Default colour" value={df.defaultColor} onChange={(c) => patch({ defaultColor: c })} />
        {(df.type === 'status_dot' || df.type === 'icon_map') && toggle('Hide value (indicator only, value on hover)', !!df.hideValue, (v) => patch({ hideValue: v }))}
      </>
    case 'date':
    case 'date_range':
      return <>
        <label style={lbl}>Date pattern</label>
        <input style={input} value={df.datePattern || ''} placeholder="dd-MMM-yyyy" onChange={(e) => patch({ datePattern: e.target.value || undefined })} />
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3 }}>Tokens: yyyy yy MMMM MMM MM dd EEE HH mm ss a</div>
        {df.type === 'date_range' && colSelect('End-date column', 'endColumn')}
      </>
    case 'relative_time':
      return <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>Shows “3 days ago” / “in 2h”; the full timestamp appears on hover.</div>
    case 'countdown':
      return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><label style={lbl}>Amber within (days)</label><input style={input} type="number" value={df.warnDays ?? 3} onChange={(e) => patch({ warnDays: Number(e.target.value) })} /></div>
        <div><label style={lbl}>Red within (days)</label><input style={input} type="number" value={df.dangerDays ?? 1} onChange={(e) => patch({ dangerDays: Number(e.target.value) })} /></div>
      </div>
    case 'elapsed':
      return <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><label style={lbl}>Amber after (minutes)</label><input style={input} type="number" min={0} value={df.warnAfterMinutes ?? ''} placeholder="—" onChange={(e) => patch({ warnAfterMinutes: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
          <div><label style={lbl}>Red after (minutes)</label><input style={input} type="number" min={0} value={df.dangerAfterMinutes ?? ''} placeholder="—" onChange={(e) => patch({ dangerAfterMinutes: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
        </div>
        {colSelect('…or amber after the minutes in column', 'warnColumn')}
        {colSelect('…or red after the minutes in column', 'dangerColumn')}
        {colSelect('Stop the clock at (column)', 'endColumn')}
        <label style={lbl}>Format</label>
        <select style={input} value={df.elapsedFormat || 'auto'} onChange={(e) => patch({ elapsedFormat: e.target.value as DisplayFormat['elapsedFormat'] })}>
          <option value="auto">m:ss, then h:mm:ss</option>
          <option value="mm:ss">minutes:seconds</option>
          <option value="h:mm">hours:minutes</option>
        </select>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3 }}>Ticks every second while shown; a per-row threshold column wins over the fixed minutes.</div>
      </>
    case 'case':
      return <>
        <label style={lbl}>Transform</label>
        <select style={input} value={df.textCase || 'title'} onChange={(e) => patch({ textCase: e.target.value as DisplayFormat['textCase'] })}>
          <option value="upper">UPPERCASE</option><option value="lower">lowercase</option>
          <option value="title">Title Case</option><option value="sentence">Sentence case</option>
          <option value="camel">camelCase</option>
        </select>
      </>
    case 'truncate':
      return <><label style={lbl}>Max characters</label><input style={input} type="number" min={1} value={df.truncate ?? 40} onChange={(e) => patch({ truncate: Number(e.target.value) })} /></>
    case 'masked':
      return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><label style={lbl}>Visible trailing chars</label><input style={input} type="number" min={0} value={df.maskLast ?? 4} onChange={(e) => patch({ maskLast: Number(e.target.value) })} /></div>
        <div><label style={lbl}>Mask character</label><input style={input} value={df.maskChar || ''} placeholder="•" onChange={(e) => patch({ maskChar: e.target.value || undefined })} /></div>
      </div>
    case 'template':
      return <><label style={lbl}>Template</label><input style={input} value={df.template || ''} placeholder="INV-{value}" onChange={(e) => patch({ template: e.target.value || undefined })} /><div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 3 }}>Use {'{value}'} and {'{column_name}'} tokens.</div></>
    case 'two_line':
      return colSelect('Secondary column (muted line)', 'secondaryColumn')
    case 'avatar':
      return <>{colSelect('Name column', 'avatarColumn', 'Defaults to this column when empty.')}{colSelect('Secondary column', 'secondaryColumn')}</>
    case 'url':
      return <><label style={lbl}>Link label</label><input style={input} value={df.label || ''} placeholder="(uses the value)" onChange={(e) => patch({ label: e.target.value || undefined })} />{colSelect('…or read the URL from column', 'hrefColumn')}</>
    case 'telephone':
      return <>
        <label style={lbl}>Label</label>
        <input style={input} value={df.label || ''} placeholder="(uses the value)" onChange={(e) => patch({ label: e.target.value || undefined })} />
        {toggle('Show country flag (from the dial code, e.g. +91 → 🇮🇳)', df.showFlag !== false, (v) => patch({ showFlag: v }))}
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>The flag appears only when the number is in international form (starts with + or 00). Otherwise a 📞 icon is shown.</div>
      </>
    case 'country':
      return <>
        <label style={lbl}>Show</label>
        <select style={input} value={df.countryShow || 'flag_name'} onChange={(e) => patch({ countryShow: e.target.value as DisplayFormat['countryShow'] })}>
          <option value="flag_name">Flag + country name</option>
          <option value="flag_code">Flag + ISO code</option>
          <option value="flag">Flag only</option>
        </select>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>The stored value can be an ISO code (IN / IND), a dial code (+91) or a country name (India).</div>
      </>
    case 'email':
    case 'file':
    case 'map':
      return <><label style={lbl}>Label</label><input style={input} value={df.label || ''} placeholder="(uses the value)" onChange={(e) => patch({ label: e.target.value || undefined })} /></>
    case 'background':
      return <RuleEditor df={df} patch={patch} />
    default:
      return null
  }
}

// ── Reusable sub-editors ─────────────────────────────────────────────────────

function ColorRow({ label, value, onChange }: { label: string; value?: string; onChange: (c: string) => void }) {
  return <div style={{ marginTop: 6 }}><ColorPickerField label={label} value={value} onChange={onChange} placeholder="e.g. green or #16a34a" /></div>
}

function toggle(label: string, value: boolean, onChange: (v: boolean) => void) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 10, cursor: 'pointer' }}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

function MapEditor({ df, patch, withIcon }: { df: DisplayFormat; patch: (p: Partial<DisplayFormat>) => void; withIcon: boolean }) {
  const map = df.map || []
  const set = (next: DisplayMapEntry[]) => patch({ map: next })
  const update = (i: number, p: Partial<DisplayMapEntry>) => set(map.map((e, idx) => idx === i ? { ...e, ...p } : e))
  return (
    <div style={{ marginTop: 8 }}>
      <label style={lbl}>Value → colour map</label>
      {map.map((e, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: withIcon ? '1fr 1fr 64px 28px' : '1fr 1fr 28px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input style={input} value={e.when} placeholder="e.g. APPROVED" onChange={(ev) => update(i, { when: ev.target.value })} />
          <input style={input} value={e.color || ''} placeholder="green / #16a34a" onChange={(ev) => update(i, { color: ev.target.value || undefined })} />
          {withIcon && <input style={input} value={e.icon || ''} placeholder="✓ ● ✕" title="A glyph/emoji character (✓ ● ✕ ⚠ ★), not an icon name" onChange={(ev) => update(i, { icon: ev.target.value || undefined })} />}
          <button style={{ ...smallBtn, padding: '4px 0' }} onClick={() => set(map.filter((_, idx) => idx !== i))}>×</button>
        </div>
      ))}
      <button style={smallBtn} onClick={() => set([...map, { when: '', color: '' }])}>+ Add value</button>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>Named colours: green, red, yellow, amber, blue, grey, teal… or any hex.</div>
    </div>
  )
}

function ThresholdEditor({ df, patch }: { df: DisplayFormat; patch: (p: Partial<DisplayFormat>) => void }) {
  const thresholds = df.thresholds || []
  const colors = df.colors || []
  const setT = (t: number[], c: string[]) => patch({ thresholds: t, colors: c })
  return (
    <div style={{ marginTop: 8 }}>
      <label style={lbl}>Base colour (below first value)</label>
      <input style={input} value={colors[0] || ''} placeholder="green / #16a34a" onChange={(e) => { const c = [...colors]; c[0] = e.target.value; setT(thresholds, c) }} />
      <label style={lbl}>Bands (value ≥ … → colour)</label>
      {thresholds.map((t, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 6, marginBottom: 6 }}>
          <input style={input} type="number" value={t} onChange={(e) => { const nt = [...thresholds]; nt[i] = Number(e.target.value); setT(nt, colors) }} />
          <input style={input} value={colors[i + 1] || ''} placeholder="colour" onChange={(e) => { const c = [...colors]; c[i + 1] = e.target.value; setT(thresholds, c) }} />
          <button style={{ ...smallBtn, padding: '4px 0' }} onClick={() => { setT(thresholds.filter((_, idx) => idx !== i), colors.filter((_, idx) => idx !== i + 1)) }}>×</button>
        </div>
      ))}
      <button style={smallBtn} onClick={() => setT([...thresholds, 0], [...colors, ''])}>+ Add band</button>
    </div>
  )
}

function RuleEditor({ df, patch }: { df: DisplayFormat; patch: (p: Partial<DisplayFormat>) => void }) {
  const rules = df.rules || []
  const set = (next: DisplayRule[]) => patch({ rules: next })
  const update = (i: number, p: Partial<DisplayRule>) => set(rules.map((r, idx) => idx === i ? { ...r, ...p } : r))
  return (
    <div style={{ marginTop: 8 }}>
      <label style={lbl}>Rules (first match wins)</label>
      {rules.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 6, marginBottom: 6 }}>
          <input style={input} value={r.when} placeholder="(budget-actual)/budget > 1.2" onChange={(e) => update(i, { when: e.target.value })} />
          <input style={input} value={r.color} placeholder="red / #dc2626" onChange={(e) => update(i, { color: e.target.value })} />
          <button style={{ ...smallBtn, padding: '4px 0' }} onClick={() => set(rules.filter((_, idx) => idx !== i))}>×</button>
        </div>
      ))}
      <button style={smallBtn} onClick={() => set([...rules, { when: '', color: '' }])}>+ Add rule</button>
      <ColorRow label="Default colour (no rule matched)" value={df.defaultColor} onChange={(c) => patch({ defaultColor: c })} />
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>Expressions may use other column names, + − × ÷, parentheses and comparisons (&gt; &lt; == !=, &amp;&amp; ||).</div>
    </div>
  )
}
